import {
    CREDIT_ORDER_TYPE,
    HTTP_STATUS_INTERNAL_ERROR,
    HTTP_STATUS_OK,
    ORDER_SIDE,
} from 'config/_const';
import AxiosConfig, { initializeJwtHeaderAndUinqueUserId } from 'config/axiosConfig';
import { ASSET_ENDPOINTS, CREDITS_ENDPOINTS, PAYMENT_ENDPOINTS } from 'config/endpoints';
import i18next from 'i18next';
import { IHttpResponse, IHttpStrongTypedResponse } from 'model/IHttpResponse';
import IBuyAsset from 'model/Payment/IBuyAsset';
import IBuyScoreBoostPayload from "model/Payment/IBuyScoreBoostPayload";
import IScoreBoostPayment from "model/Payment/IScoreBoostPayment";
import SecondaryMarketDirectBuyResponse from 'model/Payment/ISecondaryMarketDirectBuyResponse';
import { ISecondaryMarketStripePaymentResponse } from 'model/Payment/ISecondaryMarketStripePaymentResponse';
import SecondaryMarketPaymentInterface from 'model/Payment/SecondaryMarketPaymentInterface';
import { IBuyTicketParameters, ITournamentSubscription } from "model/Tournament/ITournamentSubscription";
import UserInterface from 'model/User/UserInterface';
import { Action } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { UPDATE_CREDITS_AFTER_BUY } from 'service/auth/actionsType';
import { showNotification } from 'service/notification/action';
import { dispatchSuccessSubscriptionToTournament } from "service/tournamentSubscription/actions";
import * as PaymentActionType from './actionsType';
import IBuyTournamentTicketParams from "model/Payment/IBuyTournamentTicketParams";
import { batch } from "react-redux";

const TRANSACTION_INTENT_TYPES = {
    ORDER: 'ORDER',
    BOOST_TEAM_SCORE: 'BOOST_TEAM_SCORE',
    BUY_TOURNAMENT_TICKET: 'BUY_TOURNAMENT_TICKET'
};

interface IOneOffPayment {
    assetId?: string;
    tokens?: number;
    amount?: number;
    paymentMethod?: string;
    isDirectBuy?: boolean;
    email?: string;
    currency?: string;
    sourceChannel?: string;
    sourceId?: string;
}

export const paypalPayment = (tokens: number, assetId: string): ThunkAction<void, null, unknown, Action<string>> => async (dispatch) => {
    try {
        dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
        dispatch({ type: PaymentActionType.PAYMENT_LOADING });

        const response: IHttpResponse = await AxiosConfig.post(PAYMENT_ENDPOINTS.PRIMARY_MARKET_PAYPAL_PAYMENT_BASE_URL, {
            tokens,
            assetId
        });

        if (response && response.data && response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_ERROR,
                payload: response.data.message,
            });
        }

        dispatch({
            type: PaymentActionType.PAYMENT_SUCCESS,
            payload: { data: response.data.data },
        });

        return window.location.href = response.data.data.redirectUrl;
    } catch (error) {
        return dispatch({ type: PaymentActionType.PAYMENT_ERROR });
    }
};

export const paypalPaymentSuccess = (tokens: number, assetId: string, paymentId: string, payerId: any, token: string, transactionId: string, oldTokenPrice: string): ThunkAction<void, null, unknown, Action<string>> => async (dispatch) => {

    try {
        dispatch({ type: PaymentActionType.PAYMENT_LOADING });

        const response: IHttpResponse = await AxiosConfig.get(`${PAYMENT_ENDPOINTS.PRIMARY_MARKET_PAYPAL_PAYMENT_SUCCESS_BASE_URL}?tokens=${tokens}&assetId=${assetId}&paymentId=${paymentId}&PayerID=${payerId}&token=${token}&transactionId=${transactionId}&oldTokenPrice=${oldTokenPrice}`);

        if (response && response.data && response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_ERROR,
                payload: response.data.message,
            });
        }

        dispatch({
            type: PaymentActionType.PAYMENT_SUCCESS,
            payload: { data: response.data },
        });

    } catch (error) {
        dispatch({ type: PaymentActionType.PAYMENT_ERROR });
        return window.location.href = ('/error');
    }
};

export const cryptoPayment = (tokens: number, assetId: string): ThunkAction<void, null, unknown, Action<string>> => async (dispatch) => {
    dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
    dispatch({
        type: PaymentActionType.PAYMENT_LOADING,
    });

    try {
        const response: IHttpResponse = await AxiosConfig.post(PAYMENT_ENDPOINTS.PRIMARY_MARKET_CRYPTO_PAYMENT_BASE_URL, { tokens, assetId });
        if (response && response.data && response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_ERROR,
                payload: response.data.message,
            });
        }
        return dispatch({
            type: PaymentActionType.PAYMENT_SUCCESS,
            payload: response.data.data,
        });
    } catch (error) {
        return dispatch({
            type: PaymentActionType.PAYMENT_ERROR,
        });
    }
};

export const buyAsset = (assetId: string, amount: number, isDirectBuy: boolean, priceComputed?: number, callBackAfterTxn?: () => void): ThunkAction<Promise<any>, null, unknown, Action<string>> => async (dispatch) => {
    try {
        dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
        dispatch({ type: PaymentActionType.PAYMENT_LOADING });

        const response: IHttpStrongTypedResponse<IBuyAsset> = await AxiosConfig.post(ASSET_ENDPOINTS.PRIMARY_MARKET_CREDIT_PAYMENT_BASE_URL, { assetId, amount, isDirectBuy, priceComputed });
        callBackAfterTxn?.();
        if (response && response.data && response.data.status !== HTTP_STATUS_OK) {
            showNotification(dispatch, i18next.t(response.data.message), response.data.status);
            return dispatch({
                type: PaymentActionType.PAYMENT_ERROR,
                payload: {
                    status: response.data.status,
                    message: response.data.message,
                },
            });
        }
        dispatch({
            type: UPDATE_CREDITS_AFTER_BUY,
            payload: {
                userCredits: response.data.data.userCredits,
                userVirtualCredits: response.data.data.userVirtualCredits
            }
        });
        return dispatch({
            type: PaymentActionType.PAYMENT_SUCCESS,
            payload: {
                data: response.data.data,
                status: response.data.status,
                message: response.data.message,
            },
        });
    } catch (error) {
        const exceptionAsError: Error = error as Error;
        const errorMessage = exceptionAsError.message || exceptionAsError.stack;
        callBackAfterTxn?.();
        return dispatch({
            type: PaymentActionType.PAYMENT_ERROR,
            payload: {
                status: HTTP_STATUS_INTERNAL_ERROR,
                message: errorMessage,
            },
        });
    }
};

export const getStatusAssetPayment = (transactionId: string, userId: string): ThunkAction<void, null, unknown, Action<string>> => async (dispatch) => {
    dispatch({
        type: PaymentActionType.PAYMENT_ASSET_STATUS_LOADING,
    });
    try {
        const response: IHttpResponse = await AxiosConfig.get(`${PAYMENT_ENDPOINTS.PRIMARY_MARKET_PAYMENT_STATUS_BASE_URL}${transactionId}/${userId}`);
        if (response && response.data && response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_ASSET_STATUS_ERROR,
                payload: response.data,
            });
        }
        return dispatch({
            type: PaymentActionType.PAYMENT_ASSET_STATUS_SUCCESS,
            payload: response.data,
        });
    } catch (error) {
        console.log('error from getStatusAssetPayment: ', error);
        return dispatch({
            type: PaymentActionType.PAYMENT_ASSET_STATUS_ERROR,
        });
    }
};

export const CardPayment = (assetId: string, tokens: number, isDirectBuy: boolean, paymentMethodId?: string, email?: string, currency?: string): ThunkAction<Promise<any>, null, unknown, Action<string>> => async (dispatch) => {
    return oneOffPayment({
        assetId: assetId,
        tokens: tokens,
        paymentMethod: paymentMethodId,
        isDirectBuy: isDirectBuy,
        email: email,
        currency: currency
    }, dispatch);
}

export const CreditAccountByCard = (fiatAmount: number, paymentMethodId?: string): ThunkAction<Promise<any>, null, unknown, Action<string>> => async (dispatch) => {
    return oneOffPayment({
        amount: fiatAmount,
        paymentMethod: paymentMethodId,
    }, dispatch, true);
};

const oneOffPayment = async (properties: IOneOffPayment, dispatch: ThunkDispatch<null, unknown, Action<string>>, isCredit = false) => {
    dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
    dispatch({
        type: PaymentActionType.PAYMENT_INTENT_LOADING,
    });
    try {
        const response: IHttpResponse = await AxiosConfig.post(isCredit ? CREDITS_ENDPOINTS.SECONDARY_MARKET_BUY_SHARES_WITH_STRIPE_BASE_URL : PAYMENT_ENDPOINTS.PRIMARY_MARKET_STRIPE_PAYMENT_BASE_URL, properties);
        if (response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_INTENT_ERROR,
                payload: response.data,
            });
        }
        if (response.data.data.user && response.data.data.token && properties.email) {
            const user: UserInterface = response.data.data.user;
            initializeJwtHeaderAndUinqueUserId(response.data.data.token, user._id)
        }
        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_SUCCESS,
            payload: response.data
        });
    } catch (error) {
        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_ERROR,
        });
    }
}

export const getTransactionPayment = (transactionId: string, userId: string): ThunkAction<void, null, unknown, Action<string>> => async (dispatch) => {
    dispatch({
        type: PaymentActionType.PAYMENT_TRANSACTION_LOADING,
    });
    try {
        const response: IHttpResponse = await AxiosConfig.get(`${CREDITS_ENDPOINTS.SECONDARY_MARKET_PAYMENT_STATUS_BASE_URL}${transactionId}/${userId}`);
        if (response && response.data && response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_TRANSACTION_STATUS_ERROR,
                payload: response.data,
            });
        }
        return dispatch({
            type: PaymentActionType.PAYMENT_TRANSACTION_STATUS_SUCCESS,
            payload: response.data,
        });
    } catch (error) {
        return dispatch({
            type: PaymentActionType.PAYMENT_TRANSACTION_STATUS_ERROR,
        });
    }
};

export const paypalCreditsPayment = (amount: number, tokens: number | undefined, assetId: string | undefined): ThunkAction<void, null, unknown, Action<string>> => async (dispatch) => {
    try {
        dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
        dispatch({ type: PaymentActionType.PAYMENT_LOADING });
        const data: any = {
            amount,
            intent: {
                type: TRANSACTION_INTENT_TYPES.ORDER,
                order: {
                    price: amount,
                    side: ORDER_SIDE.BUY,
                    type: CREDIT_ORDER_TYPE.LIMIT,
                    amount: tokens,
                    asset: assetId,
                },
            },
        };
        const response: IHttpResponse = await AxiosConfig.post(CREDITS_ENDPOINTS.SECONDARY_MARKET_PAYPAL_PAYMENT_BASE_URL, data);
        if (response && response.data && response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_ERROR,
                payload: response.data.message,
            });
        }
        dispatch({
            type: PaymentActionType.PAYMENT_SUCCESS,
            payload: { data: response.data.data },
        });
        return window.location.href = response.data.data.redirectUrl;
    } catch (error) {
        dispatch({ type: PaymentActionType.PAYMENT_ERROR });
    }
};

export const secondaryMarketStripePayment = (data: SecondaryMarketPaymentInterface): ThunkAction<Promise<any>, null, unknown, Action<string>> => async (dispatch) => {
    dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
    dispatch({ type: PaymentActionType.PAYMENT_LOADING });
    try {
        const payload = {
            amount: data.totalAmount,
            paymentMethod: data.paymentMethodId,
            intent: {
                type: TRANSACTION_INTENT_TYPES.ORDER,
                sellingOrderIdFromOrderbook: data.sellingOrderId,
                order: {
                    asset: data.assetId,
                    side: ORDER_SIDE.BUY,
                    type: data.type ?? CREDIT_ORDER_TYPE.MARKET,
                    price: data.priceOfOrder,
                    amount: data.amountOfShares,
                    isDirectBuy: data.isDirectBuy,
                    fromOrderbook: data.isBuyingFromOrderbook,
                    directBuyFromTheBestSellingOrders: data.directBuyFromTheBestSellingOrders
                }
            },
            email: data.email,
            currency: data.currency
        };
        const response: IHttpStrongTypedResponse<ISecondaryMarketStripePaymentResponse> = await AxiosConfig.post(`${CREDITS_ENDPOINTS.SECONDARY_MARKET_BUY_SHARES_WITH_STRIPE_BASE_URL}`, payload);
        if (response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_INTENT_ERROR,
                payload: response.data,
            });
        }
        if (response.data.data.user && response.data.data.token) {
            const user: UserInterface = response.data.data.user;
            initializeJwtHeaderAndUinqueUserId(response.data.data.token, user._id)
        }
        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_SUCCESS,
            payload: response.data
        });
    } catch (error) {
        console.log('error: ', error);
        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_ERROR,
            payload: error,
        });
    }
};

export const creditAccountWithBitpay = (amount: number, redirectUrl: string, closeUrl: string, onSuccessCallback?: (_responseData: any) => void, onErrorCallback?: () => void): ThunkAction<Promise<any>, null, unknown, Action<string>> => async (dispatch) => {
    dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
    try {
        const payload = {
            amount: amount,
            redirectUrl: redirectUrl,
            closeUrl: closeUrl
        };
        const response: IHttpResponse = await AxiosConfig.post(CREDITS_ENDPOINTS.CREDIT_ACCOUNT_WITH_BITPAY_BASE_URL, payload);
        if (response.data.status !== HTTP_STATUS_OK) {
            onErrorCallback?.();
            return dispatch({
                type: PaymentActionType.PAYMENT_INTENT_ERROR,
                payload: response.data,
            });
        }
        onSuccessCallback?.({ ...response.data.data, amount });
        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_SUCCESS,
            payload: response.data
        });
    } catch (exception) {
        onErrorCallback?.();

        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_ERROR,
            payload: exception,
        });
    }
};

export const boostTeamScoreStripePayment = (data: IScoreBoostPayment): ThunkAction<Promise<any>, null, unknown, Action<string>> => async (dispatch) => {
    dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
    dispatch({ type: PaymentActionType.PAYMENT_INTENT_LOADING });
    try {
        const payload = {
            paymentMethod: data.paymentMethodId,
            amount: data.fiatAmount,
            intent: {
                type: TRANSACTION_INTENT_TYPES.BOOST_TEAM_SCORE,
                boost: {
                    tournamentSubscriptionId: data.tournamentSubscriptionId,
                }
            },
            email: data.email,
            currency: data.currency
        };
        const response: IHttpStrongTypedResponse<ISecondaryMarketStripePaymentResponse> = await AxiosConfig.post(`${CREDITS_ENDPOINTS.SECONDARY_MARKET_BUY_SHARES_WITH_STRIPE_BASE_URL}`, payload);
        if (response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_INTENT_ERROR,
                payload: response.data,
            });
        }
        if (response.data.data.user && response.data.data.token) {
            const user: UserInterface = response.data.data.user;
            initializeJwtHeaderAndUinqueUserId(response.data.data.token, user._id)
        }
        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_SUCCESS,
            payload: response.data
        });
    } catch (error) {
        console.log('error: ', error);
        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_ERROR,
            payload: error,
        });
    }
};

export const buyTeamScoreBoostCreditPayment = (payload: IBuyScoreBoostPayload): ThunkAction<Promise<any>, null, unknown, Action<string>> => async (dispatch) => {
    dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
    dispatch({ type: PaymentActionType.PAYMENT_LOADING });
    try {
        const response: IHttpStrongTypedResponse<ISecondaryMarketStripePaymentResponse> = await AxiosConfig.post(`${CREDITS_ENDPOINTS.BOOST_SCORE_BUY_WITH_CREDIT_BASE_URL}`, payload);
        if (response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_ERROR,
                payload: response.data,
            });
        }
        return dispatch({
            type: PaymentActionType.PAYMENT_SUCCESS,
            payload: response.data
        });
    } catch (error) {
        console.log('error: ', error);
        return dispatch({
            type: PaymentActionType.PAYMENT_ERROR,
            payload: error,
        });
    }
};

export const unsafeDropPaymentData = (): ThunkAction<void, null, unknown, Action<string>> => async (dispatch) => {
    return dispatch({
        type: PaymentActionType.DROP_PAYMENT_DATA,
    });
}

export const secondaryMarketDirectBuy = (amount: number, assetId: string, price: number): ThunkAction<Promise<any>, null, unknown, Action<string>> => async (dispatch) => {
    try {
        dispatch({ type: PaymentActionType.PAYMENT_LOADING });
        const response: IHttpStrongTypedResponse<SecondaryMarketDirectBuyResponse> = await AxiosConfig.post(CREDITS_ENDPOINTS.SECONDARY_MARKET_DIRECT_BUY, { amount, assetId, price });
        if (response.data.status !== HTTP_STATUS_OK) {
            return dispatch({
                type: PaymentActionType.PAYMENT_INTENT_ERROR,
                payload: response.data,
            });
        }
        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_SUCCESS,
            payload: response.data,
        });
    } catch (error) {
        console.log('error: ', error);
        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_ERROR,
            payload: error,
        });
    }
};

export const purchaseTournamentSubscriptionTicketWithCredits = (payload: IBuyTicketParameters): ThunkAction<void, null, unknown, Action<string>> => async (dispatch) => {
    try {
        dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
        dispatch({ type: PaymentActionType.PAYMENT_LOADING });
        const response: IHttpStrongTypedResponse<ITournamentSubscription[]> = await AxiosConfig.post(`${CREDITS_ENDPOINTS.BUY_TOURNAMENT_TICKET}`, payload);
        if (response.data.status !== HTTP_STATUS_OK)
            return dispatch({
                type: PaymentActionType.PAYMENT_ERROR,
                payload: response.data,
            });
        dispatch(dispatchSuccessSubscriptionToTournament(response))
        return dispatch({
            type: PaymentActionType.PAYMENT_SUCCESS,
            payload: response.data
        });
    } catch (error) {
        return dispatch({
            type: PaymentActionType.PAYMENT_ERROR,
            payload: error,
        });
    }
};

export const actionInitiateTicketPurchaseIntentForTournament = (data: IBuyTournamentTicketParams): ThunkAction<Promise<any>, null, unknown, Action<string>> => async (dispatch) => {
    try {
        batch(() => {
            dispatch({ type: PaymentActionType.DROP_PAYMENT_DATA });
            dispatch({ type: PaymentActionType.PAYMENT_INTENT_LOADING });
        });

        const payload = {
            paymentMethod: data.paymentMethodId,
            amount: data.fiatAmount,
            intent: {
                type: TRANSACTION_INTENT_TYPES.BUY_TOURNAMENT_TICKET,
                subscription: {
                    ...data.subscription
                }
            },
        };

        const response: IHttpStrongTypedResponse<ISecondaryMarketStripePaymentResponse> = await AxiosConfig.post(`${CREDITS_ENDPOINTS.SECONDARY_MARKET_BUY_SHARES_WITH_STRIPE_BASE_URL}`, payload);
        if (response.data.status !== HTTP_STATUS_OK)
            return dispatch({
                type: PaymentActionType.PAYMENT_INTENT_ERROR,
                payload: response.data,
            });

        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_SUCCESS,
            payload: response.data
        });
    } catch (error) {
        return dispatch({
            type: PaymentActionType.PAYMENT_INTENT_ERROR,
            payload: error,
        });
    }
};
